import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { LanguageSelector, SUPPORTED_LOCALES } from "@/components/ui/language-selector";
import { Printer, FileText, Receipt } from "lucide-react";
import { useLocalization } from "@/hooks/useLocalization";

interface LanguagePrintDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  onPrint: (locale: string) => void;
  loading?: boolean;
  icon?: "printer" | "file" | "receipt";
}

export function LanguagePrintDialog({
  open,
  onOpenChange,
  title,
  onPrint,
  loading = false,
  icon = "printer"
}: LanguagePrintDialogProps) {
  const [selectedLocale, setSelectedLocale] = useState("it-IT");
  const { t } = useLocalization();

  const handlePrint = () => {
    onPrint(selectedLocale);
    onOpenChange(false);
  };

  const getIcon = () => {
    switch (icon) {
      case "file":
        return <FileText className="h-4 w-4 mr-2" />;
      case "receipt":
        return <Receipt className="h-4 w-4 mr-2" />;
      default:
        return <Printer className="h-4 w-4 mr-2" />;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            {getIcon()}
            {title}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {title}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">
              {t('documents.selectLanguage')}
            </label>
            <LanguageSelector
              value={selectedLocale}
              onChange={setSelectedLocale}
              placeholder={t('documents.selectLanguage')}
            />
          </div>
          
          <div className="text-sm text-muted-foreground">
            {t('documents.languageHelp')}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handlePrint} disabled={loading}>
            {getIcon()}
            {loading ? t('common.printing') : t('common.print')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
